import { createRef, Fragment, useEffect, useState, useMemo } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import classnames from 'classnames'

import emitter from 'garden/src/js/utils/emitter'

import * as cartRepository from 'scripts/api/cart'
import { success } from 'scripts/utils/message'

import formatPriceToBRL from 'reactUtils/formatPriceToBRL'

const CartButton = ({ cartLink }) => {
  const ESC_KEY = 27
  const [addedProducts, setAddedProducts] = useState([])
  const [count, setCount] = useState(0)
  const [showCartAddedProductsBox, setShowCartAddedProductsBox] = useState(false)
  const addedProductsBoxRef = createRef()
  const cartButtonRef = createRef()

  const variants = useMemo(() => {
    return ({
      initial: {
        opacity: 0
      },
      enter: {
        opacity: 1,
        transition: {
          duration: 1.5
        }
      },
      exit: {
        opacity: 0,
        transition: {
          duration: 3
        }
      }
    })
  }, [])

  const getCartUniqueProductsCount = async () => {
    const { data: { uniqueProductsCount } } = await cartRepository.getCartUniqueProductsCount()

    setCount(uniqueProductsCount || 0)
  }

  useEffect(() => {
    const onProductsAdded = (products, uniqueProductsCount) => {
      const mobileWidth = 768

      const isInViewport = elem => {
        const bouding = elem.getBoundingClientRect()

        return (
          bouding.top >= 0 &&
          bouding.left >= 0 &&
          bouding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          bouding.right <= (window.innerWidth || document.documentElement.clientWidth)
        )
      }

      if (window.innerWidth <= mobileWidth || !isInViewport(cartButtonRef.current)) {
        setCount(uniqueProductsCount)

        const content = products.length > 1
          ? 'Produtos adicionados ao carrinho com sucesso.'
          : 'Produto adicionado ao carrinho com sucesso.'

        return success(content)
      }

      setCount(uniqueProductsCount)
      setShowCartAddedProductsBox(true)
      setAddedProducts(products)
    }

    const onPressEscape = (e) => {
      if (e.keyCode === ESC_KEY && showCartAddedProductsBox) {
        setTimeout(() => setShowCartAddedProductsBox(), 2500)
      }
    }

    const onDocumentClick = (e) => {
      if (showCartAddedProductsBox) {
        const addToCartButton = document.querySelector('[data-component="add-to-cart"]')
        const isClickingAddToCartButton = addToCartButton.contains(e.target)
        const isClickingAddedProductsBox = addedProductsBoxRef.current.contains(e.target)

        if (!isClickingAddToCartButton && !isClickingAddedProductsBox) {
          setTimeout(() => setShowCartAddedProductsBox(false), 2500)
        }
      }
    }

    const onProductRemoved = () => setCount(c => c - 1)

    emitter.on('cart:products-added', onProductsAdded)
    emitter.on('cart:product-removed', onProductRemoved)
    document.addEventListener('click', onDocumentClick)
    document.addEventListener('keydown', onPressEscape, false)

    getCartUniqueProductsCount()

    return () => {
      document.removeEventListener('click', onDocumentClick)
      document.removeEventListener('keydown', onPressEscape)
    }
  }, [addedProductsBoxRef, cartButtonRef, showCartAddedProductsBox])

  const renderProduct = product => {
    if (!product) {
      return null
    }

    const {
      externalId,
      id,
      quantity,
      packaging,
      name,
      price: { from: priceFrom, to: priceTo, total: priceTotal },
      thumbUrl,
      unit
    } = product

    return (
      <div className="cart-added-products-box-product" key={id}>
        <div className="product-image">
          <img src={thumbUrl} />
        </div>
        <div className="product-info">
          <span className="title">{name}</span>
          <span className="small-info">Cód. {externalId}</span>

          {
            priceFrom && priceFrom !== priceTo && <span className="small-info price-from">
              {packaging} {unit} {formatPriceToBRL(priceFrom)}
            </span>
          }

          <span className="small-info">{packaging} {unit} {formatPriceToBRL(priceTo)}</span>
          <span className="price">{quantity} {unit} {formatPriceToBRL(priceTotal)}</span>
        </div>
      </div>
    )
  }

  const cartAddedProductsBoxClassnames = classnames(
    'cart-added-products-box',
    {
      'cart-added-products-box-visible': showCartAddedProductsBox === true,
      'cart-added-products-box-hidden': showCartAddedProductsBox === false
    }
  )

  const renderCartAddedProductsBox = () => {
    return (
      <div className={cartAddedProductsBoxClassnames} ref={addedProductsBoxRef}>
        <div className="cart-added-products-box-inner">
          <div className="cart-added-products-box-header">
            <span className="title">
              {addedProducts.length > 1
                ? 'Produtos adicionados ao carrinho'
                : 'Produto adicionado ao carrinho'
              }
            </span>
            <div className="line"></div>
          </div>
          {addedProducts.map(renderProduct)}
        </div>
      </div>
    )
  }

  const countClass = classnames(
    'cart-count',
    {
      mid: count >= 10,
      large: count > 99
    }
  )

  return (
    <Fragment>
      <a
        href={cartLink}
        className="cart-button"
        title="Ir para o carrinho"
        ref={cartButtonRef}
      >
        <AnimatePresence>
          {
            count >= 1 && <motion.div
              variants={variants}
              initial="initial"
              animate="enter"
              exit="exit"
            >
              <span className={classnames(countClass)}>{count}</span>
            </motion.div>
          }
        </AnimatePresence>
        <i className="glyph glyph-cart"></i>
        <span className="navigation-tools-title">carrinho</span>
      </a>
      { renderCartAddedProductsBox() }
    </Fragment>
  )
}

export default CartButton
